
import { Options, Vue } from 'vue-class-component';
import ModalBox from '@/shared/components/modal-box/index.vue';
import { Inject, Prop, Watch } from 'vue-property-decorator';
import { TrustAPI } from '@/shared/modules/trust-api';
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import { EventEmitter } from 'events';
import store from '@/trust-admin-app/store';
import V3DatePicker from 'vue3-datepicker';
import {
  FirmSearchRequest,
  InvestigationCategory,
  InvestigationRequest,
  InvestigationTypeView,
  InvestigationView,
  PracticeView,
  TrustTeamMemberView
} from '@/shared/modules/typescript-trustapi-client/models';
import NavigationBar from '@/shared/components/navigation-bar/index.vue';
import { NavItem, NavItemStatusID } from '@/shared/components/navigation-bar/navigation-item';
import debounce from 'lodash/debounce';
import moment from 'moment';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import CalendarView from '@/shared/components/calendar-view/index.vue';
import { EventInput, ViewApi } from '@fullcalendar/core';
import FormateDateTimeMixin from '@/shared/mixins/format-datetime';
import { eventTransform } from '@/shared/components/helpers';
import { orderBy } from 'lodash';

@Options({
  components: {
    ModalBox,
    V3DatePicker,
    NavigationBar,
    LoadingIndicator,
    ErrorIndicator,
    CalendarView
  },
  mixins: [FormateDateTimeMixin]
})
export default class AddEditInvestigationModal extends Vue {

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  @Inject({ from: 'investigationCategory', default: [] })
  private investigationCategory!: InvestigationCategory[];

  @Prop({ default: {} })
  private investigation!: InvestigationView;

  @Prop({ default: false})
  private isFromRoutine!: boolean;

  @Prop({ default: null})
  private routineId!: any;
  
  private formData: InvestigationView = {};

  private tableHeadings = [
    { text: 'Name', value: 'name', sortable: false },
    { text: '# Routine Investigations', value: 'routineInvestigations', sortable: false },
    { text: '# Cause driven Investigations', value: 'causeDriveInvestigations', sortable: false },
    { text: '', sortable: false },
  ];

  private tableHeadings2 = [
    { text: 'Practice (Trading name)', value: 'name', sortable: true },
    { text: 'Commencement date', value: 'date', sortable: true },
    { text: 'Type', value: 'type', sortable: true },
    { text: 'status', value: 'status', sortable: true }
  ];

  private submitting = false;
  private loadingContent = false;
  private errorLoadingContent = false;
  private loadingAvailabilityContent = false;
  private errorLoadingAvailabilityContent = false;
  private loadingTimeEntries = false;
  private errorLoadingTimeEntries = false;
  private showCancelAlert = false;

  private isOpen = false;
  private arrowCounter = -1;
  private practiceList: PracticeView[] = [];
  private search = '';
  private isSearching = false;
  private srchDebounced: any = null;

  private investigators: TrustTeamMemberView[] = [];
  private investigationTypes: InvestigationTypeView[] = [];
  private commencementDate: Date = null as any;

  private selectedDetailedInvestigator: TrustTeamMemberView = {};
  private investigations: InvestigationView[] = [];

  private selectedSection = 'Investigation list';
  private eventData: EventInput[] = [];
  private currentCalendarView: ViewApi = null as any;

  private disabledDates = new Date(moment(new Date()).add(1, 'day').toISOString());

  private investigatorOfPractice: TrustTeamMemberView[] = [];
  private loadingTrustTeamMemberHistory = false;
  private errorLoadingTrustTeamMemberHistory = false;

  private currentSelectedPractice: PracticeView = null as any;

  private navItems: NavItem[] = [
    new NavItem('Investigation list', '', null, true, NavItemStatusID.NotCurrent),
    new NavItem('Calendar view', '', null, true, NavItemStatusID.NotCurrent),
  ];

  @Watch('search')
  private onKeywordChange(): void {
    if (this.search.length > 2 && this.practiceList.filter(x => x.name == this.search).length == 0) {
      this.currentSelectedPractice = null as any;
      setTimeout(this.srchDebounced.cancel, 0);
      setTimeout(this.srchDebounced, 0);
    }
  }

  @Watch('selectedDetailedInvestigator')
  private onSelectedInvestigatorChanged(): void {
    if (this.selectedDetailedInvestigator?.id > 0) {
      if (this.selectedSection == 'Investigation list') {
        this.loadInvestigations();
      } else if (this.selectedSection == 'Calendar view') {
        this.loadTimeEntries(this.currentCalendarView);
      }
    }
  }

  @Watch('commencementDate')
  private onCommencementDateChaged(): void {
    this.getInvestigators();
  }

  @Watch('currentSelectedPractice')
  private onPracticeSelection(): void {
    this.getInvestigators();
  }

  private async searchPractice(): Promise<void> {
    const _body: FirmSearchRequest = {
      keyword: this.search,
      isExactMatch: false
    }
    try {
      this.isSearching = true;

      const resp: any = await this.apiService.PracticeApi?.apiV2PracticeSearchPracticePost(_body);
      this.practiceList = resp.data.data as PracticeView[];
      this.isOpen = this.practiceList.length > 0;
    } catch (err) {
      this.isOpen = false;
    } finally {
      this.isSearching = false;
    }
  }


  created(): void {
    this.init();
    this.updateNavItem();
  }

  mounted(): void {
    window.addEventListener('click', this.handleClickOutside);
    this.srchDebounced = debounce(() => {
      this.searchPractice();
    }, 1000);
  }

  beforeUnmount(): void {
    window.removeEventListener('click', this.handleClickOutside);
  }

  private async init(): Promise<void> {
    try {
      this.loadingContent = true;
      this.errorLoadingContent = false;
      this.practiceList = [];

      await this.preloadData();

      this.formData = JSON.parse(JSON.stringify(this.investigation));

      this.formData.id = this.formData.id ?? 0;
      this.formData.trustTeamMember = this.formData.trustTeamMember ?? {
        id: null
      };
      this.formData.latestPracticeLogData = this.formData.latestPracticeLogData ?? null;
      this.commencementDate = this.commencementDate ?? null;

      this.formData.investigationType = this.formData.investigationType ?? {
        id: null
      }

      this.formData.investigationCategory = this.formData.investigationCategory ?? {
        id: 1
      }

      if (this.formData.latestPracticeLogData) {

        const resp2: any = await this.apiService.PracticeApi?.apiV2PracticeGetPracticeByIdPracticeIdGet(
          this.formData?.latestPracticeLogData.practiceId
        );


        this.currentSelectedPractice = resp2.data.data ?? null;

        this.practiceList.push(this.currentSelectedPractice);
        this.search = this.currentSelectedPractice.name;
      }

      if (this.editMode) {
        this.commencementDate = new Date(
          moment(this.formData.commencementDate).format('YYYY-MM-DD 00:00:00')
        );
      }

    } catch (err) {
      console.log(err);
      this.errorLoadingContent = true;
    } finally {
      this.loadingContent = false;
    }
  }

  private selectInvestigationType(i: number): void {
    this.formData.investigationCategory.id = this.investigationTypes.filter(
      x => x.id == i
    )[0].defaultInvestigationCategoryId;
  }

  private updateNavItem(): void {
    this.navItems.forEach((x, i) => {
      this.navItems[i].status = NavItemStatusID.NotCurrent;
      if (x.name == this.selectedSection) {
        this.navItems[i].status = NavItemStatusID.Current;
      }
    });
  }

  private clickedNavItem(item: NavItem): void {
    this.selectedSection = item.name;
    this.updateNavItem();
    if (this.selectedSection == 'Investigation list') {
      this.loadInvestigations();
    }
  }

  private async saveInvestigation(): Promise<void> {
    try {
      if (!this.validForm && !this.isFormDirty) {
        throw new Error('Invalid Data Filled');
      }
      this.submitting = true;
      console.log(this.formData);
      const invReq: InvestigationRequest = {
        id: this.formData.id,
        investigationTypeId: this.formData.investigationType.id,
        practiceId: this.currentSelectedPractice.id,
        trustTeamMemberId: this.formData.trustTeamMember?.id,
        investigationCategoryId: this.formData.investigationCategory.id,
        commencementDate: new Date(this.commencementDate).toISOString(),
        routineId: this.routineId,
      };
      const resp: any =
        await this.apiService.InvestigationApi?.apiV2InvestigationSaveInvestigationPost(invReq);

      if (invReq.id == 0) {
        this.eventCtrl.emit('show-snackbar', 'success', 'Investigation added successfully');
      } else {
        this.eventCtrl.emit('show-snackbar', 'success', 'Investigation updated successfully');
      }
      this.eventCtrl.emit('reload-investigation-data');
      this.closeModal();
    } catch (err: any) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to add/ save investigation: ' + (err?.response?.data?.message || 'Unknown Error'));
    } finally {
      this.submitting = false;
    }
  }

  private async loadInvestigations(): Promise<void> {
    try {
      this.loadingAvailabilityContent = true;
      this.errorLoadingAvailabilityContent = false;
      this.investigations = [];
      const resp: any =
        await this.apiService.InvestigationApi?.apiV2InvestigationGetInvestigationsByTrustTeamMemberIdPost({
          trustTeamMemberId: this.selectedDetailedInvestigator.id,
          showActiveOnly: true
        });

      this.investigations = resp.data.data as InvestigationView[];

    } catch (err) {
      console.log(err);
      this.errorLoadingAvailabilityContent = true;
    } finally {
      this.loadingAvailabilityContent = false;
    }
  }

  private async loadTimeEntries(view: ViewApi): Promise<void> {
    try {
      this.loadingTimeEntries = true;
      this.errorLoadingTimeEntries = false;
      const resp: any = await this.apiService.CalendarApi?.apiV2CalendarGetEventsByTrustTeamMemberIdPost({
        trustTeamMemberId: this.selectedDetailedInvestigator.id,
        startDate: new Date(view.activeStart),
        endDate: new Date(view.activeEnd),
        showMissingTime: false
      });
      this.eventData = eventTransform(resp.data.data);
    } catch (err) {
      console.log(err);
      this.errorLoadingTimeEntries = true;
    } finally {
      this.loadingTimeEntries = false;
    }
  }


  private uiDateTimeRangeChanged(view: ViewApi): void {
    this.currentCalendarView = view;
    this.loadTimeEntries(view);
  }

  private investigatedPracticePreviously(i: TrustTeamMemberView): boolean {
    return this.investigatorOfPractice.filter(
      x => x.externalId == i.externalId
    ).length > 0;
  }

  private async getInvestigators(): Promise<void> {
    try {

      if (this.currentSelectedPractice.id > 0 && this.commencementDate) {
        this.loadingTrustTeamMemberHistory = true;
        this.errorLoadingTrustTeamMemberHistory = false;
        const resp: any = await this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberGetTrustTeamMemberByPracticeIdPost(
          {
            practiceId: this.currentSelectedPractice.id,
            commencementDate: this.commencementDate
          }
        );
        this.investigatorOfPractice = resp.data.data as TrustTeamMemberView[];
      }

    } catch (err) {
      this.errorLoadingTrustTeamMemberHistory = true;
    } finally {
      this.loadingTrustTeamMemberHistory = false;
    }
  }


  get validForm(): boolean {
    try {
      return !!this.currentSelectedPractice &&
        !!this.commencementDate &&
        !!this.formData.investigationType?.id &&
        !!this.formData.investigationCategory?.id;
    } catch {
      return false;
    }
  }

  get isFormDirty(): boolean {
    if (this.editMode) {
      const c_InvestigationType = this.investigation.investigationType?.id !==
        this.formData.investigationType.id;
      const c_InvestgationCategory = this.investigation.investigationCategory?.id !==
        this.formData.investigationCategory.id;
      const c_Investigator = this.investigation.trustTeamMember?.id !==
        this.formData.trustTeamMember.id;
      const c_CommencementDate = moment(this.investigation.commencementDate).unix() !==
        moment(new Date(this.commencementDate).toISOString()).unix();

      return c_InvestigationType || c_Investigator ||
        c_InvestgationCategory || c_CommencementDate;
    } else {
      return !!this.currentSelectedPractice;
    }
  }

  private async preloadData(): Promise<boolean> {
    try {
      const resp: any[] = await Promise.all([
        this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberGetTrustTeamMembersPost(),
        this.apiService.ConfigTrustApi?.apiV2ConfigTrustGetInvestigationTypesPost()
      ]);
      this.investigationTypes = resp[1].data.data as InvestigationTypeView[];
      this.investigators = orderBy(
        resp[0].data.data as TrustTeamMemberView[],
        [data => data.displayName.toLowerCase()],
        'asc'
      );
      this.investigators = this.investigators.filter(x => [2, 3, 5].includes(x.userGroup?.id));
      return Promise.resolve(false);
    } catch (err) {
      return Promise.reject(true);
    }
  }

  private viewDetailedInvestigator(i: TrustTeamMemberView): void {
    this.selectedDetailedInvestigator = i;
  }

  private assignInvestigator(i: TrustTeamMemberView): void {
    this.formData.trustTeamMember.id = i.id;
    this.selectedDetailedInvestigator = {};
  }

  private closeModal() {
    this.$emit('closeAddEditInvestigationModal');
  }

  get editMode(): boolean {
    try {
      return this.investigation.id > 0;
    } catch {
      return false;
    }
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  private selectPractice(p: PracticeView): void {
    this.isOpen = false;
    this.search = p.name;
    this.currentSelectedPractice = p;
  }

  private onArrowDown(evt: any) {
    if (this.arrowCounter < this.practiceList.length) {
      this.arrowCounter = this.arrowCounter + 1;
    }
  }

  private onArrowUp(): void {
    if (this.arrowCounter > 0) {
      this.arrowCounter = this.arrowCounter - 1;
    }
  }

  private onEnter() {
    if (this.practiceList.length > 0) {
      this.selectPractice(
        this.practiceList[this.arrowCounter === -1 ? 0 : this.arrowCounter]
      );
    }
    this.isOpen = false;
  }

  private handleClickOutside(evt: any): void {
    if (this.$el === (evt.target)) {
      this.isOpen = false;
      this.arrowCounter = -1;
    }
  }

  private highlightWord(word: string): string {
    try {
      const regex = new RegExp('(' + this.search + ')', 'ig');
      return word.replace(regex, '<mark>$1</mark>');
    } catch (err) {
      return word;
    }
  }

  private formatPracticeText(p: PracticeView): string {
    if (p.branchId) {
      return p.firmId + ' - ' + p.branchId + ' | ' + p.name + ' - ' + p.tradingName;
    } else {
      return p.firmId + ' | ' + p.name;
    }
  }

}

